import { QueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { format, parse } from 'date-fns';
import type { Request } from 'express';

import getMatchedRoute from '@rover/react-app/src/server/renderPage/getMatchedRoute';
import {
  apiBrightHorizonsGlobalBannerRetrieve,
  getApiBrightHorizonsGlobalBannerRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-bright-horizons-global-banner-retrieve';
import {
  apiBrightHorizonsSitterProfileBannerRetrieve,
  getApiBrightHorizonsSitterProfileBannerRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-bright-horizons-sitter-profile-banner-retrieve';
import { apiCurrentUserRetrieve } from '@rover/rsdk/src/apiClient/latest/api-current-user-retrieve';
import {
  apiFrontendConfigurationRetrieve,
  getApiFrontendConfigurationRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-configuration-retrieve';
import {
  apiPersonDetailImagesList,
  getApiPersonDetailImagesListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-person-detail-images-list';
import {
  apiPersonDetailReviewsList,
  getApiPersonDetailReviewsListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-person-detail-reviews-list';
import {
  apiPersonDetailStayMediaList,
  getApiPersonDetailStayMediaListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-person-detail-stay-media-list';
import {
  apiPersonFullSitterProfileRetrieve,
  getApiPersonFullSitterProfileRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-person-full-sitter-profile-retrieve';
import {
  apiPersonPublicCalendarRetrieve,
  getApiPersonPublicCalendarRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-person-public-calendar-retrieve';
import type { ApiPersonPublicCalendarRetrieveParams } from '@rover/rsdk/src/apiClient/latest/model/apiPersonPublicCalendarRetrieveParams';
import { HttpError } from '@rover/rsdk/src/errors';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import httpContext from '@rover/rsdk/src/modules/Network/httpContext';
import getParam from '@rover/shared/js/utils/getParams';
import { Http404Error, Http500Error } from '@rover/utilities/HttpError';

import { GALLERY_IMAGE_PAGE_SIZE } from './useGalleryImages';
import { getInitialReviewsParams, getPublicCalendarParams, isAxiosError } from './utils';

export const sitterProfileDataPrefetch = async (
  queryClient: QueryClient,
  req: Request
): Promise<void> => {
  const res = httpContext.getRes();

  const {
    pageRoute: { match },
  } = getMatchedRoute(req.path);

  const service = getParam('service_type') as string | undefined;
  const selectedPetType = getParam('pet_type') as string | undefined;
  const nocache = getParam('no_cache') as string | undefined;
  const selectedDogCount = getParam('dog_count');
  const selectedCatCount = getParam('cat_count');
  const selectedPuppyCount = getParam('puppy_count');
  const startDate = getParam('start_date');
  const selectedStartDate = startDate ? parse(startDate, 'yyyy-MM-dd', new Date()) : undefined;
  const formattedStartDate =
    selectedStartDate && !Number.isNaN(selectedStartDate.getTime())
      ? format(selectedStartDate, 'yyyy-MM-dd')
      : undefined;

  const serviceParams = service ? { service } : undefined;
  const cacheParams = nocache ? { no_cache: '1' } : undefined;
  const bhSitterProfileBannerParams = {
    serviceType: service || 'boarding',
    slug: match.params.slug,
    petType: selectedPetType || 'dog',
    dogCount: selectedDogCount ? parseInt(selectedDogCount, 10) : 0,
    catCount: selectedCatCount ? parseInt(selectedCatCount, 10) : 0,
    puppyCount: selectedPuppyCount ? parseInt(selectedPuppyCount, 10) : 0,
  };

  const calendarParams = {
    ...getPublicCalendarParams(req),
    serviceType: service,
  } as ApiPersonPublicCalendarRetrieveParams;
  let data;
  try {
    data = await queryClient.fetchQuery({
      queryKey: getApiPersonFullSitterProfileRetrieveQueryKey({
        slug: match.params.slug,
        ...serviceParams,
        ...cacheParams,
      }),
      queryFn: () =>
        apiPersonFullSitterProfileRetrieve({
          slug: match.params.slug,
          ...serviceParams,
          ...cacheParams,
        }),
    });
  } catch (error) {
    if (error instanceof Error && isAxiosError(error)) {
      Logger.warn(`Error when fetching sitter profile with slug: ${match.params.slug}`, {
        message: error.message,
        statusCode: error instanceof HttpError ? error?.statusCode.toString() : '',
        stack: error.stack || '',
      });

      if (error instanceof HttpError && error.statusCode === 404) throw new Http404Error();

      // if it is a 301, we should redirect to the provided location
      if (error instanceof HttpError && error.statusCode === 301) {
        const redirectUrl = (error.res as AxiosResponse).headers.location;
        const queryParams = req.query;
        let url = redirectUrl;
        if (Object.keys(queryParams).length > 0) {
          url += `?${new URLSearchParams(queryParams as Record<string, string>).toString()}`;
        }
        res?.redirect(url);
        return;
      }
      throw new Http500Error();
    }
  }

  if (data?.services.length === 0) {
    res?.redirect('dogs');
    return;
  }

  const isAuthenticated = data?.authenticatedContext !== null;
  const reviewsParams = getInitialReviewsParams();

  const queries: Array<Promise<any>> = [
    queryClient.fetchQuery({
      queryKey: getApiFrontendConfigurationRetrieveQueryKey({ sitterOpk: data.opk }),
      queryFn: () => apiFrontendConfigurationRetrieve({ sitterOpk: data.opk }),
    }),
    queryClient.fetchInfiniteQuery({
      queryKey: getApiPersonDetailImagesListQueryKey(data.opk),
      queryFn: () =>
        apiPersonDetailImagesList(data.opk, { page: 1, pageSize: GALLERY_IMAGE_PAGE_SIZE }),
    }),
    queryClient.fetchQuery({
      queryKey: getApiPersonPublicCalendarRetrieveQueryKey(data.opk, calendarParams),
      queryFn: () => apiPersonPublicCalendarRetrieve(data.opk, calendarParams),
    }),
    queryClient.fetchQuery({
      queryKey: getApiPersonDetailReviewsListQueryKey(data.opk, reviewsParams),
      queryFn: () => apiPersonDetailReviewsList(data.opk, reviewsParams),
    }),
    queryClient.fetchInfiniteQuery({
      queryKey: getApiPersonDetailStayMediaListQueryKey(data.opk),
      queryFn: () => apiPersonDetailStayMediaList(data.opk),
    }),
  ];

  if (isAuthenticated) {
    // Check if the user is a Bright Horizons user before fetching banner data
    let isBrightHorizonsUser = false;
    try {
      const currentUserData = await apiCurrentUserRetrieve();
      isBrightHorizonsUser =
        'currentUser' in currentUserData && currentUserData.currentUser.isBrightHorizonsUser;
    } catch (error) {
      Logger.warn('Error fetching current user data for Bright Horizons check', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }

    if (isBrightHorizonsUser) {
      queries.push(
        queryClient.fetchQuery({
          queryKey: getApiBrightHorizonsSitterProfileBannerRetrieveQueryKey(
            bhSitterProfileBannerParams
          ),
          queryFn: () => apiBrightHorizonsSitterProfileBannerRetrieve(bhSitterProfileBannerParams),
        }),
        queryClient.fetchQuery({
          queryKey: getApiBrightHorizonsGlobalBannerRetrieveQueryKey({
            isBhHub: false,
            startDate: formattedStartDate,
          }),
          queryFn: () =>
            apiBrightHorizonsGlobalBannerRetrieve({
              isBhHub: false,
              startDate: formattedStartDate,
            }),
        })
      );
    }
  }

  await Promise.all(queries);
};
